// The **driver** for one confirmed candidate selection: it issues the narrow selection write and
// settles it into the single line of copy the sandbox screen shows.
//
// It exists because `SandboxScreen.tsx` is specified as wiring (`sandbox-screen-consolidation`
// design **T1**/**T2**, asserted by `SandboxScreen.host.test.tsx`): the screen dispatches and draws,
// and it owns no request sequencing of its own. Selection is the one write that does not travel
// through `sandbox-coordinator.ts` — it relocates nothing in the working document, so it is not an
// epoched transaction and giving the reducer a seventh effect kind would claim it was. What it *does*
// have is a promise, so the promise lives here: a module with no DOM and no React, whose whole
// behaviour is reachable from a unit test rather than only through a rendered screen.
//
// The **copy** mapping travels with the sequencing rather than staying at the call site, because a
// caller that had to interpret an outcome would be back to holding the result across time. Every arm
// — recorded, each typed refusal, and a transport that rejects or throws — reaches the caller as one
// message and nothing else (`client-rounds-selection` "A refused selection is surfaced legibly":
// never a silent no-op, never a stuck pending state).

import type { SelectionOutcome } from '../../net/rounds-http';
import { candidateLabel, selectionRefusalMessage } from './selection-model';

/** The narrow selection write seam, as the screen receives it: candidate identity in, outcome out.
 * Nothing here reads editor source, per-type programs, or the birth type. */
export type MarkSelection = (candidateId: number) => Promise<SelectionOutcome>;

/** The one line shown once a selection settles. A recorded selection names the candidate the
 * **server** attributed it to (`candidateRef`) rather than the one the caller asked for, so a server
 * that recorded something else says so. */
export function selectionOutcomeMessage(outcome: SelectionOutcome): string {
  return outcome.kind === 'recorded'
    ? `${candidateLabel(outcome.candidateRef) ?? 'Candidate'} submitted for this round.`
    : selectionRefusalMessage(outcome.reason);
}

/**
 * Issues one confirmed selection and hands the caller its message exactly once.
 *
 * Synchronous, and returns nothing: the caller presses, and later hears. Whether the write was
 * refused, rejected in transport, or threw on the call itself, the caller is told once and the
 * confirmation it is driving can always leave its pending state — an unreachable hive is not a
 * different product state from one that answered `unavailable`.
 */
export function requestSelection(
  mark: MarkSelection,
  candidateId: number,
  settle: (message: string) => void,
): void {
  void settledMessage(mark, candidateId).then(settle);
}

async function settledMessage(mark: MarkSelection, candidateId: number): Promise<string> {
  try {
    return selectionOutcomeMessage(await mark(candidateId));
  } catch {
    return selectionRefusalMessage('unavailable');
  }
}
